// PEME crew status data table with expandable worklist rows
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import moment from 'moment';
import WorklistRow from './peme-status-worklist-row';
import ActionsMenu from './peme-status-actions-menu';
import './peme-status-table.scss';

const STATUS_STYLES = {
  'Fit to Work': 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-200',
  'In Clinic': 'tw-bg-indigo-50 tw-text-indigo-600 tw-border-indigo-200',
  'Pending': 'tw-bg-amber-50 tw-text-amber-600 tw-border-amber-200',
  'Pending Scheduling': 'tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200',
  'Temporarily Unfit': 'tw-bg-rose-50 tw-text-rose-600 tw-border-rose-200',
};

const COLUMNS = ['Crew Member', 'Vessel / Rank', 'Status', 'Days in Stage', 'Embarkation', 'Actions'];

// --- Sub-components ---

const Avatar = ({ name = '' }) => {
  const initials = name.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '??';
  return (
    <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-indigo-100 tw-text-indigo-700 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-semibold tw-flex-shrink-0">
      {initials}
    </div>
  );
};

const StatusBadge = ({ status }) => (
  <span className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-border ${STATUS_STYLES[status] || 'tw-bg-gray-100 tw-text-gray-600 tw-border-gray-200'}`}>
    {status}
  </span>
);

const SkeletonRow = () => (
  <tr className="tw-animate-pulse tw-border-b tw-border-slate-100">
    {Array.from({ length: 6 }).map((_, i) => (
      <td key={i} className="tw-px-4 tw-py-4">
        <div className="tw-h-4 tw-bg-slate-200 tw-rounded tw-w-3/4" />
      </td>
    ))}
  </tr>
);

// --- Embarkation cell helpers ---

const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  return moment(dateStr).startOf('day').diff(moment().startOf('day'), 'days');
};

const EmbarkationCell = ({ date }) => {
  const daysUntil = getDaysUntil(date);
  const formatted = date ? moment(date).format('DD MMM YYYY') : '—';
  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <span className="tw-text-sm tw-text-gray-800">{formatted}</span>
      {daysUntil !== null && daysUntil <= 14 && (
        <span className={`tw-self-start tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold tw-uppercase ${daysUntil <= 7 ? 'tw-bg-rose-50 tw-text-rose-600' : 'tw-bg-amber-50 tw-text-amber-600'}`}>
          {daysUntil <= 7 ? 'Urgent' : 'Approaching'}
        </span>
      )}
    </div>
  );
};

// --- Main component ---

const PemeStatusTable = ({ crewList = [], isLoading, isAdmin, expandedRowId, onExpandRow, onEdit, onViewProfile, onViewDocuments, onStatusUpdate }) => {
  return (
    <div className="peme-status-table tw-overflow-x-auto tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
      <table className="tw-w-full tw-border-collapse">
        <thead>
          <tr className="tw-bg-slate-50/50 tw-border-b tw-border-slate-100">
            {COLUMNS.map((col) => (
              <th key={col} className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-medium tw-whitespace-nowrap">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            : crewList.length === 0
            ? (
              <tr>
                <td colSpan={6} className="tw-px-4 tw-py-12 tw-text-center tw-text-sm tw-text-gray-400">
                  No crew members found.
                </td>
              </tr>
            )
            : crewList.map((crew) => {
              const isExpanded = expandedRowId === crew.id;
              const isTemporarilyUnfit = crew.status === 'Temporarily Unfit';
              const isFitToWork = crew.status === 'Fit to Work';
              const daysOverdue = !isFitToWork && crew.daysInStage >= 10;

              return (
                <React.Fragment key={crew.id}>
                  <tr className="tw-border-b tw-border-slate-100 hover:tw-bg-slate-50/50 tw-transition-colors">
                    {/* Crew Member */}
                    <td className="tw-px-4 tw-py-3">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <Avatar name={crew.name} />
                        <div>
                          <button
                            onClick={() => onViewProfile(crew)}
                            className="tw-text-sm tw-font-medium tw-text-gray-900 hover:tw-text-indigo-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-transition-colors"
                          >
                            {crew.name}
                          </button>
                          <p className="tw-text-xs tw-text-gray-500 tw-m-0">{crew.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Vessel / Rank */}
                    <td className="tw-px-4 tw-py-3">
                      <span className="tw-text-sm tw-text-gray-800">{crew.vessel}</span>
                      <p className="tw-text-xs tw-text-gray-500 tw-m-0">{crew.rank}</p>
                    </td>

                    {/* Status */}
                    <td className="tw-px-4 tw-py-3">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <StatusBadge status={crew.status} />
                        {crew.status === 'Pending' && crew.currentStage && (
                          <span className="tw-text-xs tw-text-gray-500">{crew.currentStage}</span>
                        )}
                        {isTemporarilyUnfit && (
                          <button
                            onClick={() => onExpandRow(crew.id)}
                            className="tw-p-1 tw-rounded tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
                            title={isExpanded ? 'Collapse worklist' : 'Expand worklist'}
                          >
                            {isExpanded
                              ? <ChevronUp className="tw-w-4 tw-h-4" />
                              : <ChevronDown className="tw-w-4 tw-h-4" />
                            }
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Days in Stage */}
                    <td className="tw-px-4 tw-py-3">
                      {isFitToWork ? (
                        <span className="tw-text-sm tw-text-gray-400">—</span>
                      ) : (
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <span className={`tw-text-sm ${daysOverdue ? 'tw-text-rose-600 tw-font-medium' : 'tw-text-gray-800'}`}>
                            {crew.daysInStage != null ? `${crew.daysInStage}d` : '—'}
                          </span>
                          {daysOverdue && (
                            <span className="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-semibold tw-uppercase tw-bg-rose-50 tw-text-rose-600">
                              Overdue
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Embarkation */}
                    <td className="tw-px-4 tw-py-3">
                      <EmbarkationCell date={crew.embarkationDate} />
                    </td>

                    {/* Actions */}
                    <td className="tw-px-4 tw-py-3">
                      <ActionsMenu
                        crew={crew}
                        isAdmin={isAdmin}
                        onEdit={onEdit}
                        onViewProfile={onViewProfile}
                        onViewDocuments={onViewDocuments}
                      />
                    </td>
                  </tr>

                  {/* Expandable worklist row */}
                  {isTemporarilyUnfit && isExpanded && <WorklistRow crew={crew} />}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

export default PemeStatusTable;
